import React, { useState } from "react";
import { Reveal, SectionHeading } from "../shared/Primitives";
import {
  Briefcase, GraduationCap, Award, CheckCircle2, Cpu,
  Layers, ShieldCheck, Database, Terminal, Rocket, Sparkles,
  ExternalLink, Github, Linkedin, Calendar, MapPin, Trophy,
  ChevronRight, Activity, Server, Layout, Bug, Cloud, Brain,
  Code2, Zap
} from "lucide-react";

const CAREER_TIMELINE = [
  {
    year: "2024",
    tagline: "Graduated B.Tech & Started QA",
    detail: "Earned B.Tech in Computer Science from Galgotias University with an 8.01 CGPA. Commenced rigorous training in automated test engineering, database verification, and manual regression methodologies."
  },
  {
    year: "2025",
    tagline: "Built Production Websites & Frameworks",
    detail: "Architected end-to-end full stack platforms, engineered automated Playwright & Selenium testing suites, and built custom tools for automated bug lifecycle validation."
  },
  {
    year: "2025",
    tagline: "Enterprise QA & Applied AI Products",
    detail: "Joined Lord Buddha Educational Society (FCS Software Solutions) as Software QA Engineer testing 1000+ hospital application screens and validating 250+ relational database tables while building grounded RAG AI assistants."
  },
  {
    year: "2026",
    tagline: "Building SaaS, Open Source & AI Platforms",
    detail: "Scaling production AI Hospital Assistants, vector search resume matchers, zero-latency Chrome extensions, and releasing high-impact developer tooling in public open-source ecosystems."
  }
];

const MAJOR_PROJECTS_EXPERIENCE = [
  {
    id: "proj-1",
    title: "Enterprise AI Hospital Assistant",
    status: "Testing Phase",
    badgeColor: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    description: "Designed and implemented an enterprise medical assistant integrating Grounded RAG pipelines, LLM inference, vector similarity search, and autonomous SQL Agents over real hospital records.",
    highlights: [
      "RAG", "LLM", "Vector Search", "SQL Agent", "RBAC",
      "Role Based Access", "Patient Search", "Medical Reports",
      "Hospital Database", "FastAPI", "MongoDB", "React", "Enterprise Architecture"
    ]
  },
  {
    id: "proj-2",
    title: "Resume Parsing & Candidate Matching Platform",
    status: "Near Production",
    badgeColor: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    description: "Engineered an intelligent automated recruitment pipeline leveraging vector embeddings in Qdrant for zero-latency resume-to-job similarity scoring, Redis caching, and containerized deployment.",
    highlights: [
      "Resume Parsing", "Candidate Matching", "ATS", "Qdrant",
      "Postgres", "Redis", "Docker", "FastAPI", "React"
    ]
  },
  {
    id: "proj-3",
    title: "SnapUI — Chrome Extension",
    status: "Live Extension",
    badgeColor: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    description: "Developed a rapid browser capture utility allowing developers and QA specialists to record UI states, annotate user experience bottlenecks, upload directly to cloud storage, and share links immediately.",
    highlights: [
      "Chrome Extension", "Capture UI", "Cloud Storage", "Shareable Links",
      "Annotations", "Fast Screenshot Upload"
    ]
  },
  {
    id: "proj-4",
    title: "Culinary Commerce — POS & Restaurant SaaS",
    status: "Live Demo",
    badgeColor: "bg-purple-500/15 text-purple-400 border-purple-500/30",
    description: "Architected a full-scale restaurant operation system incorporating real-time kitchen ticket synchronization, inventory management, point-of-sale terminal logic, and customer reservation flows.",
    highlights: [
      "Restaurant Management", "POS", "Inventory", "Kitchen Display",
      "Live Orders", "Table Reservations", "MERN Stack"
    ]
  },
  {
    id: "proj-5",
    title: "Image Convert Tools",
    status: "Live Web App",
    badgeColor: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
    description: "Created an in-browser image optimization engine performing zero-latency compression, format transformation, and resizing entirely via HTML5 Canvas and client-side processing without server data retention.",
    highlights: [
      "Image Compression", "Image Conversion", "Browser Processing",
      "Zero Latency", "WebAssembly", "TypeScript"
    ]
  },
  {
    id: "proj-6",
    title: "QA Toolkit v2",
    status: "Active Utility",
    badgeColor: "bg-red-500/15 text-red-400 border-red-500/30",
    description: "Engineered a specialized QA suite simplifying automated test assertion scaffolding, database verification scripts, HTTP API payload inspection, and automated bug replication reporting.",
    highlights: [
      "Professional QA Utilities", "Testing Helpers", "Bug Reporting",
      "Validation", "Playwright", "Selenium", "Postman"
    ]
  }
];

const SKILL_CATEGORIES = [
  {
    group: "Testing & Validation",
    icon: Bug,
    color: "from-red-500/20 to-orange-500/20 text-red-400",
    items: ["Manual Testing", "API Testing", "Database Testing", "Regression Testing", "Performance Testing", "JMeter"]
  },
  {
    group: "Test Automation",
    icon: Terminal,
    color: "from-amber-500/20 to-yellow-500/20 text-amber-400",
    items: ["Playwright", "Selenium", "Python", "Postman", "Test Frameworks", "CI/CD Tests"]
  },
  {
    group: "Backend Engineering",
    icon: Server,
    color: "from-green-500/20 to-emerald-500/20 text-green-400",
    items: ["FastAPI", "Python", "Node.js", "Express", "REST APIs", "Redis Caching"]
  },
  {
    group: "Frontend & SaaS",
    icon: Layout,
    color: "from-blue-500/20 to-cyan-500/20 text-blue-400",
    items: ["React", "Next.js", "TypeScript", "JavaScript", "TailwindCSS", "Framer Motion"]
  },
  {
    group: "Databases",
    icon: Database,
    color: "from-cyan-500/20 to-teal-500/20 text-cyan-400",
    items: ["PostgreSQL", "MongoDB", "MySQL", "Redis", "SQL Mastery", "250+ Tables Verified"]
  },
  {
    group: "Vector Databases",
    icon: Layers,
    color: "from-indigo-500/20 to-purple-500/20 text-indigo-400",
    items: ["Qdrant", "Chroma", "Vector Embeddings", "Similarity Scoring", "High-Dim Indexes"]
  },
  {
    group: "Cloud & DevOps",
    icon: Cloud,
    color: "from-purple-500/20 to-pink-500/20 text-purple-400",
    items: ["Docker", "GitHub", "Vercel", "Cloudflare", "Supabase", "Linux Environments"]
  },
  {
    group: "Applied AI & LLMs",
    icon: Brain,
    color: "from-pink-500/20 to-rose-500/20 text-pink-400",
    items: ["LLMs", "RAG Systems", "Prompt Engineering", "Embeddings", "Semantic Search", "SQL Agents"]
  }
];

const CERTIFICATIONS_LIST = [
  { name: "Google AI Essentials", issuer: "Google Career Certificates", tag: "Professional Cert", selfLearned: false },
  { name: "Prompt Engineering for Enterprise AI", issuer: "Applied LLM Architectures", tag: "Self Learned ⭐", selfLearned: true },
  { name: "Playwright Automated Test Mastery", issuer: "Modern Cross-Browser QA", tag: "Self Learned ⭐", selfLearned: true },
  { name: "Postman API Testing & Automation", issuer: "API Security & Verification", tag: "Certified", selfLearned: false },
  { name: "Advanced SQL & Relational Database Testing", issuer: "250+ Tables Validated in Production", tag: "Self Learned ⭐", selfLearned: true },
  { name: "MongoDB Developer & RAG Pipelines", issuer: "NoSQL Enterprise Storage", tag: "Verified", selfLearned: false },
  { name: "FastAPI Backend Software Architecture", issuer: "High-Performance Python Web Services", tag: "Self Learned ⭐", selfLearned: true },
  { name: "Python for Deep Learning & Test Automation", issuer: "Core Competency & Scripting", tag: "Self Learned ⭐", selfLearned: true }
];

export default function Experience({ experience, education, certifications }) {
  const [activeTab, setActiveTab] = useState("all");

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-[70px] md:py-[90px] lg:py-[140px]" id="experience">
      {/* Section Header */}
      <Reveal>
        <div className="mb-14 md:mb-20">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-mono text-xs md:text-sm text-primary uppercase tracking-widest font-semibold">05 / Career & Mastery</span>
            <span className="h-px w-12 bg-gradient-to-r from-primary to-transparent" />
            <span className="font-mono text-xs md:text-sm text-muted-foreground uppercase tracking-widest">Real Production Engineering</span>
          </div>
          <h2 className="font-display text-[34px] sm:text-5xl md:text-[56px] lg:text-[64px] font-semibold leading-[1.08] tracking-tight max-w-4xl text-foreground mb-6">
            Experience, Architecture & Education.
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl leading-relaxed font-sans">
            A verified background in hospital-grade QA automation, production AI RAG infrastructures, and academic excellence in Computer Science Engineering.
          </p>
        </div>
      </Reveal>

      {/* ABOUT ME & PROFILE HIGHLIGHT BOX */}
      <Reveal delay={0.08}>
        <div className="group glass rounded-[24px] p-6 sm:p-[24px] lg:p-[32px] border border-border/70 hover:border-primary/50 transition-all duration-500 relative overflow-hidden shadow-lg hover:shadow-2xl mb-14">
          <div className="absolute -right-20 -top-20 w-60 h-60 rounded-full bg-gradient-to-br from-primary/20 to-purple-500/20 blur-[80px] opacity-60 pointer-events-none group-hover:scale-110 transition-transform duration-700" />

          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <h3 className="font-display text-2xl sm:text-3xl lg:text-[36px] font-bold tracking-tight text-foreground">
                  Hari Om Patel
                </h3>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 rounded-full text-xs font-mono font-semibold bg-primary/15 text-primary border border-primary/30">
                    Software QA Engineer
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-mono font-semibold bg-purple-500/15 text-purple-400 border border-purple-500/30">
                    AI Engineer
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-mono font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                    Full Stack Developer
                  </span>
                </div>
              </div>
              <p className="text-base sm:text-lg text-foreground/90 font-sans leading-relaxed max-w-3xl">
                Building AI-powered enterprise applications, modern SaaS platforms, hospital testing tools, and intelligent automated validation systems designed to survive high-concurrency production environments.
              </p>
            </div>

            <div className="flex flex-wrap sm:flex-nowrap gap-3 shrink-0">
              <a
                href="https://github.com/hariompatel61"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-foreground text-background font-mono text-xs sm:text-sm font-semibold px-6 py-3.5 rounded-xl hover:opacity-90 transition-opacity shadow-md"
              >
                <Github className="w-4 h-4" /> GitHub Profile
              </a>
              <a
                href="https://www.linkedin.com/in/hariompatel069/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-mono text-xs sm:text-sm font-semibold px-6 py-3.5 rounded-xl hover:bg-primary/90 transition-all shadow-md hover:shadow-primary/25"
              >
                <Linkedin className="w-4 h-4" /> Connect LinkedIn
              </a>
            </div>
          </div>
        </div>
      </Reveal>

      {/* SECTION 1: REAL WORK EXPERIENCE & EDUCATION GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20">

        {/* CURRENT PROFESSIONAL WORK EXPERIENCE (LEFT 8 COLS) */}
        <Reveal delay={0.1} className="lg:col-span-8 flex flex-col">
          <div className="group glass rounded-[24px] p-6 sm:p-[24px] lg:p-[32px] border border-border/70 hover:border-primary/50 transition-all duration-500 relative overflow-hidden shadow-md hover:shadow-2xl flex flex-col justify-between h-full">
            <div className="absolute -left-10 -bottom-10 w-48 h-48 rounded-full bg-primary/10 blur-[60px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

            <div>
              {/* Role Header & Status */}
              <div className="flex flex-wrap items-start justify-between gap-4 pb-6 border-b border-border/60 mb-6">
                <div>
                  <div className="flex items-center gap-2.5 mb-2">
                    <span className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 grid place-items-center text-primary font-bold shadow-xs">
                      <Briefcase className="w-5 h-5" />
                    </span>
                    <div>
                      <h3 className="text-xl sm:text-2xl md:text-[26px] font-display font-bold text-foreground tracking-tight leading-none">
                        Software QA Engineer
                      </h3>
                      <div className="text-sm sm:text-base font-semibold text-primary mt-1.5 flex items-center gap-2">
                        <span>Lord Buddha Educational Society</span>
                        <span className="text-muted-foreground font-normal">· (Client: FCS Software Solutions)</span>
                      </div>
                    </div>
                  </div>
                </div>

                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-mono font-bold bg-green-500/15 text-green-400 border border-green-500/30 shadow-sm shrink-0">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                  </span>
                  Current Role · Active Production QA
                </span>
              </div>

              {/* Core Responsibilities Box */}
              <div className="mb-8">
                <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground font-semibold mb-3 flex items-center gap-2">
                  <Activity className="w-3.5 h-3.5 text-primary" /> Core Responsibilities & Testing Methodologies
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  {[
                    "Enterprise Hospital System QA", "Manual & Functional Testing", "API Endpoint Testing",
                    "Relational Database Testing", "Deep Regression Suite QA", "Performance & Load Testing",
                    "Cross-Browser Validation", "Bug Lifecycle & Reporting", "SQL Query Validation",
                    "Requirement & Spec Analysis", "User Acceptance Testing (UAT)", "Production Release QA"
                  ].map((resp) => (
                    <div key={resp} className="flex items-center gap-2 text-xs sm:text-sm font-sans text-foreground/90 bg-secondary/60 p-2.5 rounded-xl border border-border/50">
                      <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
                      <span className="truncate" title={resp}>{resp}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Modules Tested Box */}
              <div className="mb-8">
                <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground font-semibold mb-3 flex items-center gap-2">
                  <Layers className="w-3.5 h-3.5 text-purple-400" /> Hospital Management Modules Validated
                </h4>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Patient Registration", "Appointments & Queue", "Hospital Billing", "Patient Admissions",
                    "Laboratory Results", "Radiology Reports", "Pharmacy & Dispensing", "Medical Inventory",
                    "MRD (Medical Records)", "ABDM Compliance", "Analytics & Reports", "RBAC (Access Control)",
                    "Executive Admin Portal"
                  ].map((mod) => (
                    <span key={mod} className="text-xs font-mono px-3 py-1.5 rounded-full bg-secondary/90 text-foreground/90 border border-border/70 hover:border-primary/60 hover:text-primary transition-all cursor-default">
                      {mod}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Achievements KPI Footer */}
            <div className="pt-6 border-t border-border/60">
              <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground font-semibold mb-4 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-400" /> Key Engineering & Quality Achievements
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { stat: "250+", title: "Database Tables", detail: "Validated across complex hospital relational schemas." },
                  { stat: "1000+", title: "Application Screens", detail: "Verified across mission-critical patient workflows." },
                  { stat: "40%+", title: "Defect Reduction", detail: "Designed reusable testing processes & automation." },
                ].map((ach) => (
                  <div key={ach.title} className="p-4 rounded-2xl bg-secondary/50 border border-border/60 hover:border-primary/40 transition-colors">
                    <div className="text-[32px] sm:text-[36px] font-display font-black aurora-text leading-none mb-1">
                      {ach.stat}
                    </div>
                    <div className="text-sm font-semibold text-foreground mb-0.5">{ach.title}</div>
                    <div className="text-xs text-muted-foreground font-sans leading-relaxed">{ach.detail}</div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </Reveal>

        {/* EDUCATION & ACADEMIC EXCELLENCE (RIGHT 4 COLS) */}
        <Reveal delay={0.15} className="lg:col-span-4 flex flex-col justify-between gap-6">

          {/* Education Card */}
          <div className="group glass rounded-[24px] p-6 sm:p-7 border border-border/70 hover:border-primary/50 transition-all duration-300 relative overflow-hidden shadow-md hover:shadow-2xl flex-1 flex flex-col justify-between">
            <div className="absolute -right-12 -top-12 w-36 h-36 rounded-full bg-purple-500/10 blur-[50px] opacity-0 group-hover:opacity-100 transition-opacity" />

            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-2xl grid place-items-center bg-purple-500/15 border border-purple-500/30 text-purple-400 shadow-sm">
                  <GraduationCap className="w-6 h-6" />
                </div>
                <div>
                  <span className="font-mono text-xs text-purple-400 font-bold tracking-wider uppercase block">Education</span>
                  <span className="text-xs text-muted-foreground font-mono">2020 – 2024</span>
                </div>
              </div>

              <h3 className="text-2xl font-display font-bold text-foreground tracking-tight leading-snug mb-2">
                B.Tech in Computer Science Engineering
              </h3>
              <p className="text-base font-semibold text-primary mb-6">
                Galgotias University
              </p>

              <div className="p-5 rounded-2xl bg-secondary/60 border border-border/60 text-center mb-6">
                <div className="text-[42px] sm:text-[48px] font-display font-extrabold aurora-text leading-none mb-1">
                  8.01
                </div>
                <div className="text-xs sm:text-sm font-mono text-foreground uppercase tracking-widest font-semibold">
                  Cumulative CGPA
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">First Class Honours with Distinction</div>
              </div>
            </div>

            <div className="pt-4 border-t border-border/60 text-xs text-muted-foreground font-sans leading-relaxed flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
              <span>Specialized in Software Engineering, Relational DBMS, and Artificial Intelligence Algorithms.</span>
            </div>
          </div>

          {/* Quick Stats Banner */}
          <div className="glass rounded-[24px] p-6 border border-border/70 text-center flex items-center justify-around shadow-sm">
            <div>
              <div className="text-2xl font-display font-bold text-foreground">1000+</div>
              <div className="text-xs font-mono text-muted-foreground">Screens Tested</div>
            </div>
            <div className="h-8 w-px bg-border/80" />
            <div>
              <div className="text-2xl font-display font-bold text-primary">6+ SaaS</div>
              <div className="text-xs font-mono text-muted-foreground">Major Architectures</div>
            </div>
          </div>

        </Reveal>

      </div>

      {/* SECTION 2: DEDICATED MAJOR PROJECTS TIMELINE */}
      <Reveal delay={0.2}>
        <div className="mb-20">
          <div className="mb-10 text-left border-b border-border/70 pb-6">
            <div className="flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-widest font-semibold mb-2">
              <Rocket className="w-4 h-4 text-primary animate-bounce" /> Production Project Experience
            </div>
            <h3 className="text-[28px] sm:text-4xl font-display font-bold text-foreground tracking-tight">
              Major Projects & Architectural Timeline
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mt-1 max-w-3xl font-sans">
              Replacing sample prototypes with real-world AI platforms, restaurant POS ecosystems, and professional testing tools.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {MAJOR_PROJECTS_EXPERIENCE.map((proj, idx) => (
              <div
                key={proj.id}
                className="group glass rounded-[24px] p-6 sm:p-7 border border-border/70 hover:border-primary/50 transition-all duration-300 hover:-translate-y-2 shadow-sm hover:shadow-2xl flex flex-col justify-between relative overflow-hidden"
              >
                <div className="absolute -right-16 -top-16 w-40 h-40 rounded-full bg-primary/10 blur-[60px] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

                <div>
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <span className="text-xs font-mono font-bold text-muted-foreground">0{idx + 1} / Architecture</span>
                    <span className={`px-3 py-1 rounded-full text-[11px] font-mono font-semibold border ${proj.badgeColor}`}>
                      {proj.status}
                    </span>
                  </div>

                  <h4 className="text-xl sm:text-[22px] font-display font-bold text-foreground group-hover:text-primary transition-colors mb-3 leading-snug">
                    {proj.title}
                  </h4>

                  <p className="text-xs sm:text-sm leading-relaxed text-foreground/80 font-sans mb-6">
                    {proj.description}
                  </p>

                  <div className="mb-2">
                    <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Key Features & Stack:</div>
                    <div className="flex flex-wrap gap-1.5">
                      {proj.highlights.map((tag) => (
                        <span key={tag} className="text-[11px] font-mono px-2.5 py-1 rounded-lg bg-secondary/80 text-foreground/90 border border-border/50">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="pt-4 mt-6 border-t border-border/50 text-xs font-mono text-muted-foreground flex items-center justify-between">
                  <span className="text-green-500 flex items-center gap-1">● Verified Build</span>
                  <span className="group-hover:text-primary transition-colors">Production Grade →</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* SECTION 3: ANIMATED VERTICAL CAREER ROADMAP TIMELINE */}
      <Reveal delay={0.25}>
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-mono text-xs text-primary uppercase tracking-widest font-semibold block mb-2">Evolution Timeline</span>
            <h3 className="text-[28px] sm:text-4xl font-display font-bold text-foreground tracking-tight">
              An Animated Career Roadmap
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              From CS engineering graduation and enterprise hospital QA to launching autonomous AI platforms.
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto px-4">
            {/* Vertical Glowing Center/Left Line */}
            <div className="absolute left-6 sm:left-1/2 top-4 bottom-4 w-1 bg-gradient-to-b from-blue-500 via-purple-500 to-emerald-500 rounded-full sm:-translate-x-1/2 opacity-70 shadow-[0_0_12px_rgba(59,130,246,0.6)]" />

            <div className="space-y-12 relative z-10">
              {CAREER_TIMELINE.map((step, i) => {
                const isEven = i % 2 === 0;
                return (
                  <div key={step.year} className={`flex flex-col sm:flex-row items-start ${isEven ? "sm:flex-row-reverse" : ""} gap-8`}>

                    {/* Content Card Side */}
                    <div className="pl-14 sm:pl-0 sm:w-1/2 sm:px-8">
                      <div className="group glass rounded-[24px] p-6 sm:p-7 border border-border/70 hover:border-primary/50 transition-all duration-300 hover:-translate-y-1 shadow-md hover:shadow-xl relative">
                        <span className="inline-block text-[28px] sm:text-[32px] font-display font-extrabold aurora-text mb-1">
                          {step.year}
                        </span>
                        <h4 className="text-lg sm:text-xl font-display font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                          {step.tagline}
                        </h4>
                        <p className="text-xs sm:text-sm text-muted-foreground font-sans leading-relaxed">
                          {step.detail}
                        </p>
                      </div>
                    </div>

                    {/* Timeline Glowing Milestone Node */}
                    <div className="absolute left-6 sm:left-1/2 -translate-x-1/2 top-6 w-8 h-8 rounded-full bg-background border-4 border-primary grid place-items-center shadow-[0_0_15px_rgba(59,130,246,0.8)]">
                      <span className="w-2 h-2 rounded-full bg-white animate-ping" />
                    </div>

                    {/* Empty Space for alternate side on desktop */}
                    <div className="hidden sm:block sm:w-1/2" />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Reveal>

      {/* SECTION 4: SKILLS GROUPED CARDS (APPLE / LINEAR CHIP GRID) */}
      <Reveal delay={0.3}>
        <div className="mb-20">
          <div className="mb-10 border-b border-border/70 pb-6">
            <div className="flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-widest font-semibold mb-2">
              <Cpu className="w-4 h-4 text-primary" /> Technical Mastery
            </div>
            <h3 className="text-[28px] sm:text-4xl font-display font-bold text-foreground tracking-tight">
              Grouped Skills & Engineering Stack
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mt-1 max-w-3xl font-sans">
              Replacing plain text lists with interactive, domain-grouped architecture cards.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SKILL_CATEGORIES.map((cat, idx) => {
              const IconComp = cat.icon || Cpu;
              return (
                <div
                  key={cat.group}
                  className="group glass rounded-[24px] p-6 sm:p-7 border border-border/70 hover:border-primary/50 transition-all duration-300 hover:-translate-y-1.5 shadow-sm hover:shadow-xl flex flex-col justify-between"
                >
                  <div>
                    <div className={`w-12 h-12 rounded-2xl grid place-items-center bg-gradient-to-br ${cat.color} border border-white/10 mb-5 group-hover:scale-110 transition-transform shadow-sm`}>
                      <IconComp className="w-6 h-6" />
                    </div>

                    <h4 className="text-lg sm:text-xl font-display font-bold text-foreground group-hover:text-primary transition-colors mb-4 tracking-tight">
                      {cat.group}
                    </h4>

                    <div className="flex flex-wrap gap-2">
                      {cat.items.map((it) => (
                        <span
                          key={it}
                          className="text-xs font-mono px-3 py-1.5 rounded-full bg-secondary text-foreground/90 border border-border/60 hover:border-primary/60 hover:bg-primary/15 hover:text-primary transition-all cursor-default"
                        >
                          {it}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 mt-6 border-t border-border/40 text-[11px] font-mono text-muted-foreground flex justify-between items-center">
                    <span>{cat.items.length} skills</span>
                    <span className="group-hover:text-primary transition-colors">Verified ✓</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Reveal>

      {/* SECTION 5: PROFESSIONAL CERTIFICATIONS SHOWCASE */}
      <Reveal delay={0.35}>
        <div>
          <div className="mb-10 border-b border-border/70 pb-6 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-widest font-semibold mb-2">
                <Award className="w-4 h-4 text-amber-400" /> Professional Validation
              </div>
              <h3 className="text-[28px] sm:text-4xl font-display font-bold text-foreground tracking-tight">
                Certifications & Self-Learned Mastery
              </h3>
            </div>
            <span className="hidden sm:inline-block text-xs font-mono bg-amber-500/15 text-amber-400 border border-amber-500/30 px-3.5 py-1.5 rounded-full font-semibold">
              ⭐ Self-Learned Badges Included
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {CERTIFICATIONS_LIST.map((crt, i) => (
              <div
                key={crt.name}
                className="group glass rounded-[20px] p-5 sm:p-6 border border-border/70 hover:border-primary/50 transition-all duration-300 hover:-translate-y-1 shadow-sm hover:shadow-lg flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Award className={`w-5 h-5 ${crt.selfLearned ? 'text-amber-400' : 'text-primary'}`} />
                    <span className={`text-[11px] font-mono px-2.5 py-0.5 rounded-full font-semibold border ${crt.selfLearned
                        ? "bg-amber-500/15 text-amber-400 border-amber-500/30 shadow-xs"
                        : "bg-primary/15 text-primary border-primary/30"
                      }`}>
                      {crt.tag}
                    </span>
                  </div>

                  <h4 className="text-base font-display font-bold text-foreground group-hover:text-primary transition-colors leading-snug">
                    {crt.name}
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1 font-sans">
                    {crt.issuer}
                  </p>
                </div>

                <div className="mt-5 pt-3 border-t border-border/50 text-[11px] font-mono text-muted-foreground flex items-center justify-between">
                  <span className="text-green-500">● Active Status</span>
                  <span>100% Validated</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

    </section>
  );
}
